/**
 * LocalStorage helpers for explanation UI state:
 * - Global expand/collapse preference (last user choice becomes the new default).
 * - Per-MCQ block highlights, scoped by source (original / advanced / flashcard).
 */

const EXPAND_KEY = "lov:expl:expand-pref";
const HIGHLIGHTS_KEY = "lov:expl:highlights:v1";
const MAX_ENTRIES = 200; // simple LRU cap

export type HighlightSource = "original" | "advanced" | "flashcard";

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

/* ---------------- Expand preference ---------------- */

export function getGlobalExpandPreference(): boolean | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(EXPAND_KEY);
    if (raw === "1") return true;
    if (raw === "0") return false;
    return null;
  } catch {
    return null;
  }
}

export function setGlobalExpandPreference(expanded: boolean): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(EXPAND_KEY, expanded ? "1" : "0");
  } catch {
    /* ignore quota / privacy mode */
  }
}

/* ---------------- Highlights ---------------- */

interface HighlightStore {
  /** key = `${mcqId}:${source}`, value = ordered list of blockIds (LRU order: newest last) */
  entries: Record<string, string[]>;
  /** insertion order of keys for LRU pruning */
  order: string[];
}

function readStore(): HighlightStore {
  if (!isBrowser()) return { entries: {}, order: [] };
  try {
    const raw = window.localStorage.getItem(HIGHLIGHTS_KEY);
    if (!raw) return { entries: {}, order: [] };
    const parsed = JSON.parse(raw) as Partial<HighlightStore>;
    return {
      entries: parsed.entries && typeof parsed.entries === "object" ? parsed.entries : {},
      order: Array.isArray(parsed.order) ? parsed.order : [],
    };
  } catch {
    return { entries: {}, order: [] };
  }
}

function writeStore(store: HighlightStore): void {
  if (!isBrowser()) return;
  try {
    // LRU prune
    while (store.order.length > MAX_ENTRIES) {
      const oldest = store.order.shift();
      if (oldest) delete store.entries[oldest];
    }
    window.localStorage.setItem(HIGHLIGHTS_KEY, JSON.stringify(store));
  } catch {
    /* ignore */
  }
}

function makeKey(mcqId: string, source: HighlightSource): string {
  return `${mcqId}:${source}`;
}

export function getHighlights(mcqId: string, source: HighlightSource): Set<string> {
  if (!mcqId) return new Set();
  const store = readStore();
  const ids = store.entries[makeKey(mcqId, source)];
  return new Set(ids || []);
}

export function setHighlights(
  mcqId: string,
  source: HighlightSource,
  ids: Set<string>
): void {
  if (!mcqId) return;
  const store = readStore();
  const key = makeKey(mcqId, source);

  if (ids.size === 0) {
    delete store.entries[key];
    store.order = store.order.filter((k) => k !== key);
  } else {
    store.entries[key] = Array.from(ids);
    store.order = store.order.filter((k) => k !== key);
    store.order.push(key);
  }
  writeStore(store);
}
